import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ErrorReasonEnum } from '@error/ENUM_ErrorReason';
import { SentryService } from '@shared-lib/services/sentry.service';
import { AuthService } from '@shared/services/auth.service';
import { Observable, throwError, timer } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';

@Injectable()
export class DashlyErrorInterceptor implements HttpInterceptor {
  private readonly authService = inject(AuthService);
  private readonly sentryService = inject(SentryService);

  private readonly MAX_RETRIES = 4;
  private readonly RETRY_DELAY = 2000;

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      retry({
        count: this.MAX_RETRIES,
        delay: (error: HttpErrorResponse) => {
          if (error?.status === 0) {
            return timer(this.RETRY_DELAY);
          }
          throw error;
        },
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status !== 0 && error.status !== 401 && error.status !== 503) {
          this.sentryService.captureHttpError(req, error, this.authService.userIdFromToken());
        }

        if (error.status === 401 && (this.isInvalidTokenError(error) || !this.isAuthServiceRequest(req))) {
          this.authService.logoutAndRedirect();
        }

        return throwError(() => error);
      }),
    );
  }

  private isInvalidTokenError(error: HttpErrorResponse): boolean {
    const reason: ErrorReasonEnum | undefined = error.error?.reason;

    return (
      reason === ErrorReasonEnum.JwtTokenExpired ||
      reason === ErrorReasonEnum.JwtTokenInvalidApplication ||
      reason === ErrorReasonEnum.JwtTokenInvalidToken
    );
  }

  private isAuthServiceRequest(req: HttpRequest<unknown>): boolean {
    return req.url.includes('/auth-service/');
  }
}
